import type { ReactNode } from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import { format } from "date-fns";
import {
  formatCurrencyGbpCompact,
  formatCount,
} from "@shared-core/domain/formatters";
import type * as API from "@shared-core/data/api-types";
import { NotResearched } from "./NotResearched";

interface CustomerFirmographicSummaryProps {
  description?: string | null;
  ukTurnoverGbp?: number | null;
  ukTurnoverAsOf?: string | null;
  ukTurnoverConfidence?: API.Confidence | null;
  ukEmployees?: number | null;
  ukEmployeesAsOf?: string | null;
  ukEmployeesConfidence?: API.Confidence | null;
  sector?: string | null;
  website?: string | null;
  /**
   * True when enrichment has run at least once for this customer. Distinguishes
   * "not yet researched" (show CTA tooltip) from "researched but no public
   * information available" (show neutral tooltip).
   */
  isEnriched?: boolean;
  /** Notes count, rendered inline at the end of the line as a subtle link. */
  notesCount?: number;
  onOpenNotes?: () => void;
  /**
   * Enrichment provenance ("Enriched 4 hours ago" + its refresh control),
   * rendered last on the meta line. A slot rather than props because the
   * refresh is a page-level action with its own in-flight state; this
   * component only decides *where* provenance sits — next to the figures it
   * describes, not beside the customer's name.
   */
  enrichmentStatus?: ReactNode;
}

function formatEmployees(count: number): string {
  return new Intl.NumberFormat("en-GB").format(count);
}

function formatAsOf(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return format(new Date(iso), "MMM yyyy");
  } catch {
    return null;
  }
}

/**
 * Small inline hint surfacing enrichment provenance (as-of date) and
 * confidence when it matters. HIGH confidence stays invisible — only
 * MEDIUM and LOW render a badge so the reader knows when to discount.
 */
function ProvenanceCaption({
  asOf,
  confidence,
}: {
  asOf: string | null | undefined;
  confidence: API.Confidence | null | undefined;
}) {
  const asOfLabel = formatAsOf(asOf);
  const showConfidence = confidence === "medium" || confidence === "low";
  if (!asOfLabel && !showConfidence) return null;

  return (
    <span className="text-xs text-muted-foreground/70 ml-1">
      {asOfLabel && `(as of ${asOfLabel})`}
      {asOfLabel && showConfidence && " "}
      {showConfidence && (
        <span className="uppercase tracking-wide font-medium">
          {confidence === "medium" ? "med" : "low"} conf
        </span>
      )}
    </span>
  );
}

function cleanWebsiteLabel(url: string): string {
  // Strip protocol + trailing slash for compact display. Keep path if present.
  return url
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "")
    .replace(/^www\./, "");
}

/**
 * Header-level firmographic one-liner. Identity-domain content ("who are
 * they") that persists across every tab. Surfaces unknowns as a soft CTA
 * because teaching users more about their own customers is part of the
 * product's job.
 */
export function CustomerFirmographicSummary({
  description,
  ukTurnoverGbp,
  ukTurnoverAsOf,
  ukTurnoverConfidence,
  ukEmployees,
  ukEmployeesAsOf,
  ukEmployeesConfidence,
  sector,
  website,
  isEnriched,
  notesCount,
  onOpenNotes,
  enrichmentStatus,
}: CustomerFirmographicSummaryProps) {
  const descriptionText = description?.trim() || null;
  const websiteUrl = website?.trim() || null;

  return (
    <div className="space-y-2">
      {descriptionText && (
        <p className="text-sm text-foreground/80 leading-relaxed">
          {descriptionText}
        </p>
      )}
      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
        <Sparkles className="size-3 text-muted-foreground/70" />
        <span>
          {ukTurnoverGbp != null && ukTurnoverGbp > 0 ? (
            <>
              <span className="font-medium text-foreground">
                {formatCurrencyGbpCompact(ukTurnoverGbp)}
              </span>{" "}
              turnover
              <ProvenanceCaption
                asOf={ukTurnoverAsOf}
                confidence={ukTurnoverConfidence}
              />
            </>
          ) : (
            <NotResearched isEnriched={isEnriched}>
              {isEnriched ? "Turnover not found" : "Turnover unknown"}
            </NotResearched>
          )}
        </span>
        <span className="text-muted-foreground/40">·</span>
        <span>
          {ukEmployees != null && ukEmployees > 0 ? (
            <>
              <span className="font-medium text-foreground">
                {formatEmployees(ukEmployees)}
              </span>{" "}
              staff
              <ProvenanceCaption
                asOf={ukEmployeesAsOf}
                confidence={ukEmployeesConfidence}
              />
            </>
          ) : (
            <NotResearched isEnriched={isEnriched}>
              {isEnriched ? "Staff count not found" : "Staff unknown"}
            </NotResearched>
          )}
        </span>
        <span className="text-muted-foreground/40">·</span>
        <span>
          {sector?.trim() ? (
            <>
              <span className="font-medium text-foreground">{sector}</span>{" "}
              sector
            </>
          ) : (
            <>
              Sector{" "}
              <NotResearched isEnriched={isEnriched}>
                {isEnriched ? "not found" : "not yet researched"}
              </NotResearched>
            </>
          )}
        </span>
        {websiteUrl && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {cleanWebsiteLabel(websiteUrl)}
              <ExternalLink className="size-3" />
            </a>
          </>
        )}
        {notesCount != null && onOpenNotes && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <button
              onClick={onOpenNotes}
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline cursor-pointer"
            >
              {formatCount(notesCount)} note{notesCount === 1 ? "" : "s"}
            </button>
          </>
        )}
        {enrichmentStatus && (
          <>
            <span className="text-muted-foreground/40">·</span>
            {enrichmentStatus}
          </>
        )}
      </div>
    </div>
  );
}
